import {
    BlockedItem,
    BulkExecutionCounts,
    BulkPreflightResult,
    PreflightOperatorReportMessage,
} from 'src/models/promotion/BulkPromotionTypes';
import { BulkPromotionStatus } from 'src/models/promotion/bulkPromotionEnums';

/**
 * Reading a batch record, and folding report frames into a verdict.
 *
 * These sit beside the other bulk helpers rather than with the wire shapes because they are logic, not
 * declarations: the readers normalise the two spellings the service and the stored record disagree on,
 * and the fold has to survive a frame arriving twice or never arriving at all.
 */

/**
 * Folds one report frame into the verdict the confirmation prompt reads.
 *
 * Pure, and keyed on page number, so a frame delivered twice does not duplicate blocked rows and a
 * frame that never arrives leaves pagesReceived below totalPages instead of silently shortening the
 * list. `prerequisites` is carried over from what is already held: the asynchronous report has no
 * prerequisite roll-up, and dropping it would empty a table the operator was already reading.
 */
export function mergePreflightFrame(
    existing: BulkPreflightResult | null,
    frame: PreflightOperatorReportMessage,
    seenPages: number[]
): { verdict: BulkPreflightResult; seenPages: number[] } {
    const { summary } = frame;
    const alreadySeen = seenPages.includes(frame.pageNumber);
    const nextPages = alreadySeen ? seenPages : [...seenPages, frame.pageNumber];

    const carriedBlocked = existing?.blocked ?? [];
    const incoming: BlockedItem[] = alreadySeen
        ? []
        : (frame.blockedComponents || []).map((row) => ({
              componentId: row.componentId,
              reason: row.reason,
              detail: row.detail || '',
              missingDependency: row.missingDependency,
          }));

    return {
        seenPages: nextPages,
        verdict: {
            promotable: summary.promotableComponentIds || [],
            blocked: [...carriedBlocked, ...incoming],
            prerequisites: existing?.prerequisites ?? [],
            // Each component lands in exactly one of the three lists, so their sum is what was accepted;
            // the summary carries no acceptedCount. Counts fall back to their id list then to zero: they are
            // typed as required but unvalidated on arrival, and isCompleteVerdict withholds a NaN verdict
            // rather than showing it, leaving the operator never asked to confirm.
            acceptedCount:
                (summary.promotableCount ?? summary.promotableComponentIds?.length ?? 0) +
                (summary.blockedCount ?? summary.blockedComponentIds?.length ?? 0) +
                (summary.skippedCount ?? summary.skippedComponentIds?.length ?? 0),
            validationMode: summary.validationMode,
            skipped: summary.skippedComponentIds || [],
            fullyValidated: summary.fullyValidated,
            pagesReceived: nextPages.length,
            totalPages: frame.totalPages,
            // Same fallback as the sum. Left bare, an omitted count reads as unknown though the id list
            // carries it, and consumers then use the row count — the value this field exists to differ from.
            blockedCount: summary.blockedCount ?? summary.blockedComponentIds?.length ?? 0,
        },
    };
}

/**
 * A batch parked on its verdict. Not terminal and not running: the state machine has stopped and
 * will not move again until the operator confirms, so this is the status the resume path rebuilds a
 * confirmation prompt from rather than reporting progress for.
 */
export function isAwaitingConfirmation(status?: string): boolean {
    return status === BulkPromotionStatus.AWAITING_CONFIRMATION;
}

/**
 * The batch id off a summary or detail row, under either spelling.
 *
 * The Smithy shapes call it `batchId`; the shapes declared in this file call it `bulkPromotionId`
 * and the promotion-history surfaces read that. Rather than rename a field those surfaces depend
 * on, readers that must work either way go through here.
 */
export function readBatchId(record: any): string | undefined {
    return record?.batchId ?? record?.bulkPromotionId;
}

/** The batch status off a summary or detail row, under either spelling. */
export function readBatchStatus(record: any): string | undefined {
    return record?.status ?? record?.bulkPromotionStatus;
}

/**
 * The five counts off a summary or detail row, whether nested under `counts` as the Smithy shape
 * has them or flat as the shapes in this file do.
 */
export function readBatchCounts(record: any): BulkExecutionCounts {
    const nested = record?.counts;
    return {
        requested: nested?.requested ?? record?.requestedCount ?? 0,
        promoted: nested?.promoted ?? record?.promotedCount ?? 0,
        blocked: nested?.blocked ?? record?.blockedCount ?? 0,
        failed: nested?.failed ?? record?.failedCount ?? 0,
        skipped: nested?.skipped ?? record?.skippedCount ?? 0,
    };
}
